/**
 * Algolia Insights Tracking for Product Detail Page (PDP)
 * Sends viewedObjectIDs event for Advanced Personalization & Algolia Recommend
 * 
 * Note: viewedFilters is NOT used by Advanced Personalization (only Classic).
 * Affinities for vendor, category, etc. are calculated automatically from
 * the attributes of products viewed via viewedObjectIDs.
 */
(function() {
  'use strict';

  // Wait for DOM to be ready, then wait a bit for other scripts to initialize
  document.addEventListener('DOMContentLoaded', function() {
    // Wait 500ms to ensure other Algolia scripts have set the userToken
    setTimeout(function() {
      initAlgoliaPDPTracking();
    }, 500);
  });

  async function initAlgoliaPDPTracking() {
    // Check if we have the product data and Algolia Insights
    if (typeof window.algoliaProductData === 'undefined') {
      console.warn('[Algolia PDP Tracking] No product data found');
      return;
    }

    if (typeof window.aa === 'undefined') {
      console.warn('[Algolia PDP Tracking] Algolia Insights (aa) not loaded');
      return;
    }

    const productData = window.algoliaProductData;
    
    // Try to get userToken - should already be set by theme.liquid
    let userToken = localStorage.getItem('ais_user_id');
    
    // If no userToken (shouldn't happen normally), try to get from cookie or generate
    if (!userToken) {
      // Try to get from cookie 1st party
      const cookieMatch = document.cookie.match(/algolia_user_token=([^;]+)/);
      if (cookieMatch) {
        userToken = cookieMatch[1];
        localStorage.setItem('ais_user_id', userToken);
        console.log('[Algolia PDP Tracking] UserToken restored from cookie:', userToken);
      } else {
        // Generate fallback with same format as theme.liquid
        userToken = Date.now() + '_' + Math.floor(Math.random() * 1000000000);
        localStorage.setItem('ais_user_id', userToken);
        document.cookie = 'algolia_user_token=' + userToken + ';path=/;max-age=31536000;SameSite=Lax';
        console.log('[Algolia PDP Tracking] Generated fallback userToken:', userToken);
      }
    }

    // Get Algolia config
    const indexName = localStorage.getItem('algolia_Index') || 'shopify_theoblistproducts';

    // ============================================
    // 1. Fetch product from Algolia via proxy to get correct objectID
    // ============================================
    let algoliaObjectId = null;
    
    console.log('[Algolia PDP Tracking] Algolia config:', { indexName });
    
    if (productData.handle) {
      try {
        const algoliaProduct = await searchProductInAlgolia(indexName, productData.handle);
        console.log('[Algolia PDP Tracking] Found product in Algolia:', algoliaProduct?.title);
        
        if (algoliaProduct && algoliaProduct.objectID) {
          algoliaObjectId = String(algoliaProduct.objectID);
        }
      } catch (error) {
        console.warn('[Algolia PDP Tracking] Could not search in Algolia, using local data:', error);
      }
    } else {
      console.warn('[Algolia PDP Tracking] Missing handle, cannot fetch product data');
    }

    // ============================================
    // 2. Déterminer la source de la visite
    // ============================================
    const currentSource = localStorage.getItem('ais_source');
    const referrer = document.referrer || '';
    const urlParams = new URLSearchParams(window.location.search);
    
    // IMPORTANT: Ne PAS écraser si la source est déjà "search" ou "autocomplete"
    // Ces sources sont définies par le clic sur un résultat de recherche et ont un queryID associé
    const isFromAlgoliaSearch = currentSource === 'search' || currentSource === 'autocomplete';
    
    // Vérifier si on vient d'un clic interne (source déjà définie récemment)
    // ou si c'est une visite directe/externe
    const isInternalNavigation = currentSource && 
      (referrer.includes(window.location.hostname) || referrer === '');
    
    // Si pas de navigation interne détectée ET pas depuis une recherche Algolia, détecter la source
    if (!isInternalNavigation && !isFromAlgoliaSearch) {
      // Détecter les sources marketing via UTM ou autres paramètres
      const utmSource = urlParams.get('utm_source');
      const utmMedium = urlParams.get('utm_medium');
      const gclid = urlParams.get('gclid'); // Google Ads
      const fbclid = urlParams.get('fbclid'); // Facebook Ads
      
      let detectedSource = 'direct';
      
      if (fbclid) {
        detectedSource = 'facebook-ads';
      } else if (gclid) {
        detectedSource = 'google-ads';
      } else if (utmSource) {
        detectedSource = `utm-${utmSource}`;
      } else if (referrer && !referrer.includes(window.location.hostname)) {
        // Vient d'un site externe (email, autre site, etc.)
        detectedSource = 'external-referral';
      }
      
      localStorage.setItem('ais_source', detectedSource);
      localStorage.setItem('ais_search_term', '');
      console.log('[Algolia PDP Tracking] Source detected:', detectedSource);
    } else if (isFromAlgoliaSearch) {
      console.log('[Algolia PDP Tracking] Keeping Algolia search source:', currentSource);
    }

    // ============================================
    // 3. Stocker l'objectID pour le tracking Add to Cart
    // ============================================
    const objectIdToSend = algoliaObjectId || String(productData.variant_id || productData.id);
    localStorage.setItem('ais_product_id', objectIdToSend);
    console.log('[Algolia PDP Tracking] Product ID stored:', objectIdToSend);
    
    // ============================================
    // 4. Send viewedObjectIDs event
    // Note: viewedObjectIDsAfterSearch n'existe pas dans le SDK Algolia
    // Les événements "view" ne sont pas utilisés par Dynamic Re-Ranking
    // (seuls click, addToCart, purchase sont utilisés)
    // ============================================
    try {
      aa('viewedObjectIDs', {
        eventName: 'Product Page Viewed',
        index: indexName,
        objectIDs: [objectIdToSend],
        userToken: userToken
      });
      console.log('[Algolia PDP Tracking] viewedObjectIDs sent with objectID:', objectIdToSend);
    } catch (error) {
      console.error('[Algolia PDP Tracking] Error sending viewed event:', error);
    }
  }

  // ============================================
  // Search product in Algolia by handle (via AlgoliaProxy)
  // ============================================
  async function searchProductInAlgolia(indexName, handle) {
    if (typeof window.AlgoliaProxy === 'undefined' || typeof window.AlgoliaProxy.search !== 'function') {
      throw new Error('AlgoliaProxy.search not available');
    }

    const response = await window.AlgoliaProxy.search([
      {
        indexName: indexName,
        query: handle,
        params: { hitsPerPage: 5, analytics: false }
      }
    ]);

    const hits = (response.results && response.results[0] && response.results[0].hits) || [];
    const product = hits.find(hit => hit.handle === handle);

    if (!product) {
      throw new Error(`Product not found with handle: ${handle}`);
    }

    return product;
  }

})();
